import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import type { LoginRequest } from '../types';

// Brief 2026-10-08-004: Lao by default (LanguageProvider's default when nothing is saved), with a
// ລາວ / English switch on the page. The switch uses the app's own language setting, so the
// choice carries into the app after login. "VTW Integration Hub" is the product name.
const T = {
  lo: {
    subtitle: 'ລະບົບຄຸ້ມຄອງລູກຄ້າ',
    username: 'ຊື່ຜູ້ໃຊ້',
    password: 'ລະຫັດຜ່ານ',
    showPassword: 'ສະແດງລະຫັດຜ່ານ',
    hidePassword: 'ເຊື່ອງລະຫັດຜ່ານ',
    signIn: 'ເຂົ້າສູ່ລະບົບ',
    signingIn: 'ກຳລັງເຂົ້າສູ່ລະບົບ...',
    forgot: 'ລືມລະຫັດຜ່ານ?',
    invalid: 'ຊື່ຜູ້ໃຊ້ ຫຼື ລະຫັດຜ່ານບໍ່ຖືກຕ້ອງ',
    unreachable: 'ເຊື່ອມຕໍ່ເຊີບເວີບໍ່ໄດ້. ກະລຸນາກວດເບິ່ງການເຊື່ອມຕໍ່ ແລ້ວລອງໃໝ່.',
    inactive: 'ບັນຊີຜູ້ໃຊ້ນີ້ຖືກປິດໃຊ້ງານ',
    driverAppOnly: 'ບັນຊີນີ້ໃຊ້ສຳລັບແອັບຄົນຂັບເທົ່ານັ້ນ.',
    language: 'ພາສາ',
  },
  en: {
    subtitle: 'Customer Management System',
    username: 'Username',
    password: 'Password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    signIn: 'Sign in',
    signingIn: 'Signing in...',
    forgot: 'Forgot your password?',
    invalid: 'Invalid username or password',
    unreachable: 'Cannot reach the server. Please check your connection and try again.',
    inactive: 'User account is inactive',
    driverAppOnly: 'This account is for the driver app.',
    language: 'Language',
  },
};

// A server `detail` is shown as sent; our own messages are kept as keys so they follow a
// language switch.
type MessageKey = 'invalid' | 'unreachable' | 'inactive' | 'driverAppOnly';
type LoginError = { detail: string } | { key: MessageKey } | null;

// Row 44: the backend refuses a login with fixed English texts (backend/app/api/v1/endpoints/
// users.py, POST /users/login). Each known one is shown as our own message so it follows the page
// language. The backend text itself stays as it is — other clients may match on it.
const KNOWN_DETAILS = new Map<string, MessageKey>([
  ['Incorrect username or password', 'invalid'],
  ['User account is inactive', 'inactive'],
  ['This account is for the driver app.', 'driverAppOnly'],
]);

// Row 67: `detail` is not always a string. A 422 from FastAPI sends a list of {loc, msg, type},
// and some handlers send an object such as {field, message} — or a list of them (V17.516). Rendering either as-is throws React
// error #31 and blanks the page. Returns '' when there is no readable text, so the caller falls
// back to our own `invalid` message.
function detailText(detail: unknown): string {
  const text = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (!item || typeof item !== 'object') return '';
        const { message, msg } = item as { message?: unknown; msg?: unknown };
        return text(message) || text(msg);
      })
      .filter(Boolean)
      .join('; ');
  }
  if (detail && typeof detail === 'object') {
    const { message, msg } = detail as { message?: unknown; msg?: unknown };
    return text(message) || text(msg);
  }
  return '';
}

export default function Login() {
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();
  const t = T[language] ?? T.lo;
  const { login, isAuthenticated, isLoading: authLoading, getDefaultRoute, needsRoleSelection } = useAuth();
  const [formData, setFormData] = useState<LoginRequest>({
    username: '',
    password: '',
  });
  const [error, setError] = useState<LoginError>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // V6.0 Workspace: Redirect to role-appropriate workspace if already authenticated
  // V17.71: Redirect multi-role users to /select-role instead
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      if (needsRoleSelection) {
        navigate('/select-role', { replace: true });
      } else {
        const defaultRoute = getDefaultRoute();
        navigate(defaultRoute, { replace: true });
      }
    }
  }, [isAuthenticated, authLoading, navigate, getDefaultRoute, needsRoleSelection]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await login(formData.username, formData.password);
      // V17.59.3: Navigation is handled by the useEffect that watches isAuthenticated
      // Do NOT call getDefaultRoute() here — permissions state hasn't updated yet
    } catch (err: any) {
      // No response means the request never reached the backend (server down,
      // network failure) — don't blame the credentials for that.
      const detail = detailText(err.response?.data?.detail);
      const known = KNOWN_DETAILS.get(detail);
      setError(
        err.response
          ? known
            ? { key: known }
            : detail
              ? { detail }
              : { key: 'invalid' }
          : { key: 'unreachable' }
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen min-h-[100dvh] flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div
          role="group"
          aria-label={t.language}
          className="flex justify-end gap-1 text-sm"
        >
          {(['lo', 'en'] as const).map((lang) => (
            <button
              key={lang}
              type="button"
              aria-pressed={language === lang}
              onClick={() => setLanguage(lang)}
              className={`inline-flex min-h-[44px] min-w-[44px] items-center justify-center px-3 py-1 rounded-md border ${
                language === lang
                  ? 'bg-indigo-600 border-indigo-600 text-white'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              {lang === 'lo' ? 'ລາວ' : 'English'}
            </button>
          ))}
        </div>
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            VTW Integration Hub
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            {t.subtitle}
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="username" className="sr-only">
                {t.username}
              </label>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                required
                className="appearance-none rounded-none relative block w-full min-h-[44px] px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 text-base sm:text-sm"
                placeholder={t.username}
                value={formData.username}
                onChange={(e) =>
                  setFormData({ ...formData, username: e.target.value })
                }
              />
            </div>
            <div className="relative">
              <label htmlFor="password" className="sr-only">
                {t.password}
              </label>
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                className="appearance-none rounded-none relative block w-full min-h-[44px] px-3 py-2 pr-12 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 text-base sm:text-sm"
                placeholder={t.password}
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex w-11 min-h-[44px] items-center justify-center z-20"
                tabIndex={-1}
                aria-label={showPassword ? t.hidePassword : t.showPassword}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-300">
                    {'detail' in error ? error.detail : t[error.key]}
                  </h3>
                </div>
              </div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-base sm:text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isLoading ? t.signingIn : t.signIn}
            </button>
          </div>

          <div className="flex items-center justify-center">
            <Link
              to="/forgot-password"
              className="inline-flex min-h-[44px] min-w-[44px] items-center px-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500"
            >
              {t.forgot}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
